import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Banner, Button, FormControl, LoadingButton, Modal, Spinner } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';
import { tryParseJson } from '~src/utils/json';

import { useCarrierContractAuthMutation } from '../mutations';
import { SchemaFormObject } from '../schema/form/schema-form-object';
import { useCarrierContractSchemaQuery } from '../schema/queries';

interface Props {
  onAuthComplete: (data: object) => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  schemaId: string;
  isOpen: boolean;
}

function useCarrierContractAuthFormSchema(schemaId: string, step: number) {
  const query = useCarrierContractSchemaQuery(schemaId);

  return {
    ...query,
    data: query.data?.authentication_form_json_schema?.[`step_${step}`],
  };
}

export function AuthFormModal({ isOpen, schemaId, setIsOpen, onAuthComplete }: Props) {
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string>();
  const [step, setStep] = useState(0);

  const { data: schema, isLoading } = useCarrierContractAuthFormSchema(schemaId, step);
  const { t } = useTranslation();

  const authMutation = useCarrierContractAuthMutation();
  const modal = useRef<HTMLDivElement>(null);
  const form = useForm();

  function handleOpenChange(value: boolean) {
    /**
     * This means we're closing the dialog, and should therefore restart the step process
     */
    if (!value) {
      /**
       * We use a timeout here to wait for the modal transition
       * to complete in order to avoid flickering.
       */
      setTimeout(() => {
        form.reset();
        setError(undefined);
        setIsComplete(false);
        setStep(0);
      }, 500);
    }

    setIsOpen(value);
  }

  async function handleSubmit(payload: Record<string, unknown>) {
    const response = await authMutation.mutateAsync({
      form_input: { step_input: { data: JSON.stringify(payload[`step_${step}`]), number: step } },
      schema_id: schemaId,
    });

    if (response.form_output?.is_flow_completed && response.form_output.data) {
      onAuthComplete(tryParseJson(response.form_output.data)!);
      setError(undefined);
      setIsComplete(true);
      return;
    }

    if (response.form_output?.step_output?.next_step_number) {
      setStep(response.form_output?.step_output.next_step_number);
      setError(undefined);
      return;
    }

    setError(response.form_output?.step_output?.error_message ?? t('errors.unknown'));
    modal.current?.scrollTo(0, 0);
  }

  return (
    <FormProvider {...form}>
      <FormControl.Root>
        {/* eslint-disable-next-line react-hooks/refs */}
        <FormControl.Form onSubmit={form.handleSubmit(payload => handleSubmit(payload))} style={{ display: 'none' }} />

        <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
          <Modal.Content>
            <Modal.Header title={t('carrierContracts.create.authForm.title')} />

            <Modal.Body ref={modal}>
              {isLoading && <Spinner />}
              {error && <Banner variant="critical" title={t('errors.generic')} subtitle={error} />}
              {schema && !isComplete && <SchemaFormObject schemaId={schemaId} schema={schema} path={`step_${step}`} />}
              {isComplete && (
                <Banner
                  subtitle="The authentication is now complete, you can now close this dialog and complete creating your contract."
                  title="Credentials retreived"
                  variant="success"
                />
              )}
            </Modal.Body>

            <Modal.Footer>
              <Modal.CancelButton />
              {isComplete ? (
                <Modal.CloseButton>
                  <Button>{t('common.close')}</Button>
                </Modal.CloseButton>
              ) : (
                <FormControl.SubmitButton asChild>
                  <LoadingButton loading={authMutation.isPending}>{t('common.next')}</LoadingButton>
                </FormControl.SubmitButton>
              )}
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>
      </FormControl.Root>
    </FormProvider>
  );
}
